/**
 * TopHeader Component
 * Slack/Discord-style top header with logo, campaign selector, and user menu.
 */

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import {
  Settings,
  CircleUserRound,
  LogOut,
  Lock,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores';
import { CampaignSelector } from '@/components/campaign';
import { getBrandAsset } from '@/themes';
import { cn } from '@/lib/utils';
import { useUnacknowledgedTaskCount } from '@/hooks/queries/useTasks';

function MyTasksIndicator() {
  const countRead = useUnacknowledgedTaskCount(undefined);
  const count = countRead.status === 'ready' ? countRead.data.count : null;
  const navigate = useNavigate();
  const location = useLocation();
  const prevCountRef = useRef(0);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (count === null) return;
    if (count > prevCountRef.current && prevCountRef.current > 0) {
      const newCount = count - prevCountRef.current;
      setToast(`You have ${newCount} new task assignment${newCount > 1 ? 's' : ''}`);
    }
    prevCountRef.current = count;
  }, [count]);

  // Auto-dismiss the toast after 5s (parity with the old MUI Snackbar autoHideDuration).
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleClick = () => {
    if (location.pathname === '/app/home') {
      window.dispatchEvent(new Event('pulse-new-tasks'));
    } else {
      navigate({ to: '/app/home' });
    }
  };

  return (
    <>
      {count !== null && count > 0 && (
        <button
          onClick={handleClick}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
        >
          <span>My tasks</span>
          <span className="bg-primary text-primary-foreground text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1">
            {count}
          </span>
        </button>
      )}
      {toast && (
        <div
          role="status"
          className="fixed bottom-4 left-4 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg"
        >
          {toast}
        </div>
      )}
    </>
  );
}

export function TopHeader() {
  const { user, logout } = useAuth();
  const presetKey = useCampaignStore((s) => s.selectedCampaign?.presetKey);
  const isCampaignOwner = useCampaignStore((s) => s.selectedCampaign?.isOwner);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between pl-14 pr-2 md:px-4 shrink-0">
      {/* Left: Logo */}
      <Link to="/app" className="hidden md:flex items-center rounded-lg bg-logo-surface px-3 py-1.5">
        <img
          src={getBrandAsset('logo.svg')}
          alt="Logo"
          className="h-7"
        />
      </Link>

      {/* Center: Campaign Selector + Task Indicator */}
      <div className="flex-1 min-w-0 flex items-center justify-center gap-3 max-w-lg mx-2 md:mx-4">
        <CampaignSelector onCreateCampaign={() => navigate({ to: '/get-started' })} />
        <MyTasksIndicator />
      </div>

      {/* Right: User Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-md',
              'text-sm text-gray-700 hover:bg-gray-100 transition-colors'
            )}
          >
            <CircleUserRound className="h-6 w-6 text-gray-500" />
            <span className="hidden sm:inline font-medium truncate max-w-32">
              {user?.email?.split('@')[0]}
            </span>
            <ChevronDown className="h-5 w-5 text-gray-400" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            {user?.email}
          </DropdownMenuLabel>
          <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">
            Role: {isCampaignOwner ? 'Owner' : (presetKey ?? (user?.isPlatformAdmin ? 'Admin' : ''))}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/app/settings">
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/app/settings">
              <Lock className="h-4 w-4" />
              Change Password
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export default TopHeader;
